import { AdminTenantKey, KeyWrappedBy } from '../../../models/admin.models';
import { DateUtils } from '../../../../utils/date-utils';

export interface KeyTableColumn {
  field: string;
  header: string;
  sortable?: boolean;
  hasTooltip?: boolean;
  scrollable?: boolean;
}

export interface KeyTypeSection {
  type: string;
  displayName: string;
  keys: AdminTenantKey[];
  columns: KeyTableColumn[];
}

const KNOWN_TYPE_ORDER = [
  'tenant-kek',
  'client-kek',
  'pin-bdk',
  'p2pe-bdk',
  'mk-cg',
  'mk-tr',
  'sk'
];

export function formatTypeDisplayName(type: string): string {
  return type
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
}

export function formatFieldHeader(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

export function getWrappedByKeyName(key: AdminTenantKey): string | undefined {
  if (key.wrappedBy === KeyWrappedBy.TenantKek) {
    return key.tenantKekName;
  }
  if (key.wrappedBy === KeyWrappedBy.HostMkTr || key.wrappedBy === KeyWrappedBy.HostMkCg) {
    return key.mkName;
  }
  return undefined;
}

function enrichKeysWithSearchableFields(keys: AdminTenantKey[]): AdminTenantKey[] {
  return keys.map(key => ({
    ...key,
    createdAtFormatted: key.createdAt ? DateUtils.formatDateTime(key.createdAt) : ''
  }));
}

export function getColumnsForKeyType(type: string, keys: AdminTenantKey[]): KeyTableColumn[] {
  switch (type) {
    case 'client-kek':
    case 'tenant-kek':
      return [
        { field: 'name', header: 'Name' },
        { field: 'version', header: 'Version' },
        { field: 'algorithm', header: 'Algorithm' },
        { field: 'region', header: 'Region' },
        { field: 'kcv', header: 'KCV' },
        { field: 'isDefault', header: 'Is Default' },
        { field: 'keyAlias', header: 'Key Alias' },
        { field: 'createdAt', header: 'Created At' }
      ];
      
    case 'mk-tr':
    case 'mk-cg':
      return [
        { field: 'name', header: 'Name' },
        { field: 'kcv', header: 'KCV' },
        { field: 'wrappedKeyBlock', header: 'Wrapped Key Block', scrollable: true },
        { field: 'wrappedBy', header: 'Wrapped By' },
        { field: 'wrappedByKeyName', header: 'Wrapped By Key Name', hasTooltip: true },
        { field: 'createdAt', header: 'Created At' }
      ];
      
    case 'pin-bdk':
    case 'p2pe-bdk':
      return [
        { field: 'name', header: 'Name' },
        { field: 'kcv', header: 'KCV' },
        { field: 'ksi', header: 'KSI' },
        { field: 'wrappedKeyBlock', header: 'Wrapped Key Block', scrollable: true },
        { field: 'wrappedBy', header: 'Wrapped By' },
        { field: 'wrappedByKeyName', header: 'Wrapped By Key Name', hasTooltip: true },
        { field: 'createdAt', header: 'Created At' }
      ];
      
    case 'sk':
      return [
        { field: 'name', header: 'Name' },
        { field: 'kcv', header: 'KCV' },
        { field: 'wrappedKeyBlock', header: 'Wrapped Key Block', scrollable: true },
        { field: 'wrappedBy', header: 'Wrapped By' },
        { field: 'wrappedByKeyName', header: 'Wrapped By Key Name', hasTooltip: true },
        { field: 'createdAt', header: 'Created At' }
      ];
      
    default:
      return getColumnsForUnknownType(keys);
  }
}

function getColumnsForUnknownType(keys: AdminTenantKey[]): KeyTableColumn[] {
  if (keys.length === 0) return [];
  
  const allFields = new Set<string>();
  keys.forEach(key => {
    Object.keys(key).forEach(field => {
      if (field !== 'type' && field !== 'createdAtFormatted') {
        allFields.add(field);
      }
    });
  });
  
  return Array.from(allFields).map(field => ({
    field,
    header: formatFieldHeader(field),
    scrollable: field === 'wrappedKeyBlock',
    hasTooltip: field === 'wrappedByKeyName'
  }));
}

export function organizeKeysByType(keys: AdminTenantKey[]): KeyTypeSection[] {
  const validKeys = keys.filter(key => key.type);
  const enrichedKeys = enrichKeysWithSearchableFields(validKeys);
  
  const keysByType = new Map<string, AdminTenantKey[]>();
  enrichedKeys.forEach(key => {
    if (!keysByType.has(key.type)) {
      keysByType.set(key.type, []);
    }
    keysByType.get(key.type)!.push(key);
  });
  
  const sortedTypes = Array.from(keysByType.keys()).sort((a, b) => {
    const aIndex = KNOWN_TYPE_ORDER.indexOf(a);
    const bIndex = KNOWN_TYPE_ORDER.indexOf(b);
    
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });
  
  return sortedTypes.map((type, index) => {
    const typeKeys = keysByType.get(type)!;
    return {
      type,
      displayName: formatTypeDisplayName(type),
      keys: typeKeys,
      columns: getColumnsForKeyType(type, typeKeys)
    };
  });
}
